import React, { useState, useEffect } from 'react';

// PWA Status Component
export const PWAStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Listen for online/offline events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Only show status when offline - no need to show "installed" banner
  if (!isOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 bg-red-500 text-white px-4 py-2 text-center z-50">
        📡 Mode hors ligne - Certaines fonctionnalités peuvent être limitées
      </div>
    );
  }

  return null;
};

// Hook for PWA functionality
export const usePWA = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isInstalled, setIsInstalled] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Check installation status
    setIsInstalled(
      window.navigator.standalone === true || 
      window.matchMedia('(display-mode: standalone)').matches
    );

    // Online/offline detection
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Service worker update detection
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        setUpdateAvailable(true);
      });
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const refreshApp = () => {
    window.location.reload();
  };

  return {
    isOnline,
    isInstalled,
    updateAvailable,
    refreshApp
  };
};

// Install prompt component
export const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const checkInstalled = () => {
      setIsInstalled(
        window.navigator.standalone === true || 
        window.matchMedia('(display-mode: standalone)').matches
      );
    };

    checkInstalled();

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Only show prompt if not already installed
      if (!isInstalled) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isInstalled]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`Installation result: ${outcome}`);
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDeferredPrompt(null);
  };

  // Don't show install prompt if already installed or if showPrompt is false
  if (!showPrompt || isInstalled) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 max-w-md mx-auto">
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          <img src="/medias/icons/icon-72x72.png" alt="SLR" className="w-12 h-12 rounded-lg" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-900">
            Installer Médiathèque - Saint-Louis Ruche
          </h3>
          <p className="text-xs text-gray-500">
            Accès rapide depuis votre écran d'accueil
          </p>
        </div>
      </div>
      <div className="mt-3 flex space-x-2">
        <button
          onClick={handleInstall}
          className="flex-1 bg-blue-500 text-white text-sm py-2 px-4 rounded-md hover:bg-blue-600 transition-colors"
        >
          Installer
        </button>
        <button
          onClick={handleDismiss}
          className="flex-1 bg-gray-200 text-gray-800 text-sm py-2 px-4 rounded-md hover:bg-gray-300 transition-colors"
        >
          Plus tard
        </button>
      </div>
    </div>
  );
};

// Update notification component
export const UpdateNotification = () => {
  const { updateAvailable, refreshApp } = usePWA();

  if (!updateAvailable) {
    return null;
  }

  return (
    <div className="fixed top-4 left-4 right-4 bg-blue-500 text-white rounded-lg shadow-lg p-4 z-50 max-w-md mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">Nouvelle version disponible !</h3>
          <p className="text-xs opacity-90">Actualisez pour obtenir les dernières fonctionnalités</p>
        </div>
        <button
          onClick={refreshApp}
          className="bg-white text-blue-500 text-sm py-2 px-4 rounded-md hover:bg-gray-100 transition-colors"
        >
          Actualiser
        </button>
      </div>
    </div>
  );
};